"use client";

import Link from "next/link";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import type { HomeEmotionalConnectionSectionContent } from "@/features/home/types/home-page-content";

interface HomeEmotionalConnectionSectionProps {
  content: HomeEmotionalConnectionSectionContent;
}

export function HomeEmotionalConnectionSection({ content }: HomeEmotionalConnectionSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Moves the background on scroll to create a parallax effect.
  // The background container is made taller (h-[140%]) so we have room to move it.
  const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  return (
    <section
      ref={containerRef}
      className="relative z-[40] flex h-[80vh] min-h-[600px] w-full items-center overflow-hidden"
    >
      {/* Parallax Background */}
      <motion.div style={{ y }} className="absolute inset-0 -z-20 h-[140%] w-full">
        <Image
          src="/images/home/emotional-connection-bg.png"
          alt={content.backgroundImageAlt}
          fill
          className="object-cover object-[center_30%]"
        />
      </motion.div>

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 -z-10 bg-black/40" />

      {/* Content Area */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-2xl text-white"
        >
          <h2 className="l font-heading text-4xl font-light tracking-tight text-balance text-white sm:text-5xl lg:text-6xl">
            {content.title}
          </h2>

          <div className="mt-8 space-y-6 text-lg leading-relaxed font-light text-white/90 sm:text-xl">
            {content.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <p className="font-medium text-white">{content.emphasis}</p>
          </div>

          <Link
            className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-secondary px-8 py-4 text-sm font-semibold text-secondary-foreground shadow-lg transition-all hover:scale-105 hover:bg-secondary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            href={content.ctaHref}
          >
            {content.ctaLabel}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
